/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useCallback } from 'react';
import type { FormatXMLElementFn, Options, PrimitiveType } from 'intl-messageformat';
import { dismissFlag as createDismissFlag, useFlagService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import { defineMessage, useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import type { CrossJoinIntegration } from '../../common/types.tsx';
import { getDynamicMessages, type EntityId } from '../../services/dynamic-messaging/index.tsx';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';
import {
	RequestAccessPromptFlag as RequestAccessPromptFlagCommon,
	type CJNMessageStrings,
} from './flag-common.tsx';

const titleMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.title',
	defaultMessage: 'Your team is now using Confluence',
	description: 'Title of invite prompt flag to join Confluence',
});

const descriptionMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description',
	defaultMessage:
		'Organize team knowledge, capture ideas, and share requirements for your Jira projects.',
	description: 'Description of how Confluence can be used to assist with Jira projects',
});

const joinButtonMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.join-button',
	defaultMessage: 'Join your team',
	description: 'Action to direct access or request access to Confluence',
});

const remindMeLaterButtonMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.remind-me-later-button',
	defaultMessage: 'Remind me later',
	description: 'Title of the dismiss button in the request access prompt flag',
});

const notInterestedButtonMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.not-interested-button',
	defaultMessage: 'Not interested',
	description: 'Title of the dismiss button with a stronger tone in the request access prompt flag',
});

const urgencyTitleMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.urgency-title',
	defaultMessage:
		'{viewCount, select, 0 {Your team is now using Confluence} 1 {Your team is working in Confluence} other {Your team is waiting for you in Confluence}}',
	description: 'Title of invite prompt flag to join Confluence that with increasing urgency',
});

const titleJpdNudgeMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.title-jpd-nudge',
	defaultMessage: 'Join your team in using Jira Product Discovery',
	description: 'Title of invite prompt flag to join Jira Product Discovery',
});

const descriptionJpdNudgeMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description-jpd-nudge',
	defaultMessage:
		'Prioritize ideas, share custom roadmaps, and unify your workflow from discovery to delivery.',
	description: 'Description of how Jira Product Discovery can be used to assist with Jira projects',
});

const titleLoomNudgeMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.title-loom-nudge',
	defaultMessage: 'Join your team on Loom',
	description: 'Title of invite prompt flag to join Loom',
});

const descriptionLoomNudgeMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description-loom-nudge',
	defaultMessage: 'Use Loom videos to record your screen, demos, and reviews.',
	description: 'Description of how Loom can be used to assist with Jira projects',
});

const descriptionLoomNudgeWithOneCollaboratorMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description-loom-nudge-with-one-collaborators',
	defaultMessage:
		'{collaboratorOne} is using Loom videos to record their screen, demos, and reviews.',
	description:
		'Description of how Loom can be used to assist with Jira projects when collaborators are present',
});

const descriptionLoomNudgeWithTwoCollaboratorsMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description-loom-nudge-with-two-collaborators',
	defaultMessage:
		'{collaboratorOne} and {collaboratorTwo} are using Loom videos to record their screen, demos, and reviews.',
	description:
		'Description of how Loom can be used to assist with Jira projects when collaborators are present',
});

const descriptionLoomNudgeWithMoreThanTwoCollaboratorsMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.description-loom-nudge-with-more-than-two-collaborators',
	defaultMessage:
		'{collaboratorOne} and {collaboratorCount} others are using Loom videos to record their screen, demos, and reviews.',
	description:
		'Description of how Loom can be used to assist with Jira projects when collaborators are present',
});

const getStartedButtonMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.get-started-button',
	defaultMessage: 'Get started',
	description: 'Action to direct access or request access to a product',
});

const doNotShowAgainButtonMessage = defineMessage({
	id: 'cross-join-nudges.common.request-access-prompt-flag.do-not-show-again-button',
	defaultMessage: "Don't show again",
	description: 'Title of the dismiss button with a stronger tone in the request access prompt flag',
});

const messages = {
	title: titleMessage,
	description: descriptionMessage,
	joinButton: joinButtonMessage,
	remindMeLaterButton: remindMeLaterButtonMessage,
	notInterestedButton: notInterestedButtonMessage,
	urgencyTitle: urgencyTitleMessage,
	titleJpdNudge: titleJpdNudgeMessage,
	descriptionJpdNudge: descriptionJpdNudgeMessage,
	titleLoomNudge: titleLoomNudgeMessage,
	descriptionLoomNudge: descriptionLoomNudgeMessage,
	descriptionLoomNudgeWithOneCollaborator: descriptionLoomNudgeWithOneCollaboratorMessage,
	descriptionLoomNudgeWithTwoCollaborators: descriptionLoomNudgeWithTwoCollaboratorsMessage,
	descriptionLoomNudgeWithMoreThanTwoCollaborators:
		descriptionLoomNudgeWithMoreThanTwoCollaboratorsMessage,
	getStartedButton: getStartedButtonMessage,
	doNotShowAgainButton: doNotShowAgainButtonMessage,
};

export const useRequestAccessPromptFlag = (): (({
	users,
	onOptout,
	onSubmit,
	snoozeFor30Days,
	snoozeFor7Days,
	integration,
	eventAttributes,
	messageParser,
	featureVisitedCount,
	onDisplayed,
}: {
	users: Collaborators;
	onOptout: () => unknown;
	onSubmit: () => Promise<void>;
	snoozeFor30Days: () => unknown;
	snoozeFor7Days?: () => unknown;
	messageParser: (formatter: {
		(
			descriptor: MessageDescriptor,
			values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>,
			opts?: Options,
		): string;
		(
			descriptor: MessageDescriptor,
			values?:
				| Record<
						string,
						| string
						| number
						| boolean
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						| React.ReactElement<any, string | React.JSXElementConstructor<any>>
						| Iterable<React.ReactNode>
						| React.ReactPortal
						| Date
						| FormatXMLElementFn<React.ReactNode, React.ReactNode>
						| null
						| undefined
				  >
				| undefined,
			opts?: Options,
		): React.ReactNode;
	}) => CJNMessageStrings;
	eventAttributes: AnalyticsAttributes;
	integration: CrossJoinIntegration;
	sessionId?: string;
	featureVisitedCount?: number;
	onDisplayed: () => void;
}) => void) => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	return useCallback(
		({
			users,
			onOptout,
			onSubmit,
			snoozeFor30Days,
			snoozeFor7Days,
			integration,
			eventAttributes,
			messageParser,
			featureVisitedCount = 0,
			onDisplayed,
		}: {
			users: Collaborators;
			onOptout: () => unknown;
			onSubmit: () => Promise<void>;
			snoozeFor30Days: () => unknown;
			snoozeFor7Days?: () => unknown;
			messageParser: (formatter: typeof formatMessage) => CJNMessageStrings;
			eventAttributes: AnalyticsAttributes;
			integration: CrossJoinIntegration;
			sessionId?: string;
			featureVisitedCount?: number;
			onDisplayed: () => void;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', integration, eventAttributes);
				dismissTheFlag();
				await onSubmit();
			};

			const dismissAndOptout = () => {
				dismissTheFlag();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag optedOut', integration, eventAttributes);
				onOptout();
			};

			const dismissAndSnooze = () => {
				dismissTheFlag();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', integration, {
					...eventAttributes,
					snoozeDays: 30,
				});
				snoozeFor30Days();
			};

			const dismiss = () => {
				const isFirstVisit = featureVisitedCount === 0;
				// Snooze for 7 days if dismissFor7Days is enabled and snoozeFor7Days function is provided
				if (isFirstVisit) {
					if (snoozeFor7Days) {
						dismissTheFlag();
						fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', integration, {
							...eventAttributes,
							snoozeDays: 7,
						});
						snoozeFor7Days();
					} else {
						dismissTheFlag();
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'flag dismissed',
							integration,
							eventAttributes,
						);
					}
				} else {
					dismissAndSnooze();
				}
			};

			const cjnMessages = messageParser(formatMessage);
			const avatarOrientation =
				integration === 'crossJoinNudgesJiraToJpd' || integration === 'crossJoinNudgesJiraToLoom'
					? 'horizontal'
					: 'vertical';

			const handleDisplayed = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', integration, eventAttributes);
				onDisplayed();
			};

			showFlag({
				id,
				render: (props) => (
					<RequestAccessPromptFlagCommon
						{...props}
						id={id}
						users={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onOptoutAndDismiss={dismissAndOptout}
						onSubmit={submit}
						avatarOrientation={avatarOrientation}
						messages={cjnMessages}
						featureVisitedCount={featureVisitedCount}
						onDisplayed={handleDisplayed}
					/>
				),
			});
		},
		[formatMessage, createAnalyticsEvent, dismissFlag, showFlag],
	);
};

export function secondsElapsedSince(timestampInMs: number): string {
	const currentTime = Date.now();
	return ((currentTime - timestampInMs) / 1000).toFixed(3);
}

type CommonAnalyticsArgs = {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
	startTimestamp: number;
	snoozeDays?: number;
};
export const getAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	featureVisitedCount,
	startTimestamp,
	snoozeDays,
}: CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		sessionId,
		featureVisitedCount,
		eligibilityLatencySeconds: secondsElapsedSince(startTimestamp),
		snoozeDays,
	};
};

export const getContextualBanditAnalyticsAttributes = ({
	entityId,
	isContextualBandit,
	...args
}: {
	entityId: EntityId;
	isContextualBandit: boolean;
} & CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		...getAnalyticsAttributes(args),
		isContextualBandit,
		entityId,
	};
};

export const getContextualBanditCJNMessageParser = ({
	isContextualBandit,
	entityId,
	featureVisitedCount,
}: {
	isContextualBandit: boolean;
	entityId: EntityId;
	featureVisitedCount: number;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		const dynamicMessages = getDynamicMessages(isContextualBandit, entityId) || messages;
		const infoFlagTitle = isContextualBandit
			? formatMessage(messages.title)
			: formatMessage(messages.urgencyTitle, { viewCount: featureVisitedCount });

		return {
			title: infoFlagTitle,
			description: formatMessage(dynamicMessages.description),
			joinButton: formatMessage(dynamicMessages.joinButton),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};

export const getJPDCJNMessageStrings = (
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
): CJNMessageStrings => {
	return {
		title: formatMessage(messages.titleJpdNudge),
		description: formatMessage(messages.descriptionJpdNudge),
		joinButton: formatMessage(messages.getStartedButton),
		remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
		notInterestedButton: formatMessage(messages.doNotShowAgainButton),
	};
};

export const getLoomCJNMessageStrings = ({
	collaborators,
}: {
	collaborators: Collaborators;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		const getDescription = () => {
			if (collaborators.length === 0) {
				return formatMessage(messages.descriptionLoomNudge);
			}

			const [first, second] = collaborators;

			let description;

			if (collaborators.length === 1) {
				description = formatMessage(messages.descriptionLoomNudgeWithOneCollaborator, {
					collaboratorOne: first.name,
				});
			} else if (collaborators.length === 2) {
				description = formatMessage(messages.descriptionLoomNudgeWithTwoCollaborators, {
					collaboratorOne: first.name,
					collaboratorTwo: second.name,
				});
			} else {
				description = formatMessage(messages.descriptionLoomNudgeWithMoreThanTwoCollaborators, {
					collaboratorOne: first.name,
					collaboratorCount: collaborators.length - 1,
				});
			}

			return description;
		};

		return {
			title: formatMessage(messages.titleLoomNudge),
			description: getDescription(),
			joinButton: formatMessage(messages.getStartedButton),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};
